'use client';

import { useState, type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  PASSWORD_RULES_ISSUE,
  type PasswordFormValues,
} from '@eliadmin/core/src/components/nt/account/password/password-schema';
import { InputAction, TextInput } from '@eliadmin/core/src/components/nt/forms/controls';
import { describedBy, FormField } from '@eliadmin/core/src/components/nt/forms/form-field';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export const passwordFieldId = (name: keyof PasswordFormValues) =>
  `password-${name.replace(/_/g, '-')}`;

/**
 * Figma „Form / Input“ with the „Zobraziť“ action (207:11374): a required password field bound to
 * the change-password form. The action reveals the value („Skryť“ hides it again). `success` is the
 * matched repeat – #C4E6CB border and a green helper; `describes` adds the id of an element that
 * explains the field (the rule list).
 */
export function PasswordField({
  name,
  label,
  autoComplete,
  hint,
  success,
  describes,
}: {
  name: keyof PasswordFormValues;
  label: string;
  autoComplete: 'current-password' | 'new-password';
  hint?: ReactNode;
  success?: boolean;
  describes?: string;
}) {
  const { t } = useTranslations();
  const [visible, setVisible] = useState(false);
  const { register, getFieldState, formState } = useFormContext<PasswordFormValues>();
  const fieldError = getFieldState(name, formState).error;
  const id = passwordFieldId(name);
  // The unmet rules turn red in the list the field points to – no error line of their own.
  const error =
    fieldError?.message && fieldError.message !== PASSWORD_RULES_ISSUE
      ? fieldError.message
      : undefined;
  const ariaDescribedBy = [describedBy(id, { error, hint }), describes].filter(Boolean).join(' ');

  return (
    <FormField id={id} label={label} required hint={hint} error={error}>
      <TextInput
        id={id}
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        autoCapitalize="off"
        spellCheck={false}
        aria-required
        invalid={!!fieldError}
        aria-describedby={ariaDescribedBy || undefined}
        boxClassName={cn(success && !fieldError && 'border-[#C4E6CB]')}
        action={
          <InputAction onClick={() => setVisible(value => !value)}>
            {visible ? t('text3256') : t('text2076')}
          </InputAction>
        }
        {...register(name)}
      />
    </FormField>
  );
}
